import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectFeatureFlagsState } from '../store/featureFlagsSlice';
import {
  FeatureFlagKey,
  FEATURE_FLAG_DEFAULT_REASONS,
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_LABELS,
} from '../types/featureFlags';
import './FeatureFlagsBanner.css';

interface BannerItem {
  key: FeatureFlagKey;
  label: string;
  reason: string;
}

const DISMISS_STORAGE_KEY = 'featureFlags.bannerDismissed';

/**
 * Глобальный баннер в шапке: показывается, когда хотя бы один feature-флаг отключён.
 * Самодостаточный — слушает Redux, без props.
 *
 * Управление видимостью:
 *   - Скрывается, если пользователь нажал «×» именно для текущего набора отключённых
 *     ключей+reason. Если что-то поменялось (другой ключ упал / включился обратно /
 *     обновили reason) — баннер снова появится.
 *   - Не появляется, если ни одного отключённого флага нет.
 */
const FeatureFlagsBanner: React.FC = () => {
  const flags = useSelector(selectFeatureFlagsState);

  const disabledItems = useMemo<BannerItem[]>(() => {
    if (!flags) return [];
    return FEATURE_FLAG_KEYS.filter((k) => flags[k]?.enabled === false).map((k) => ({
      key: k,
      label: FEATURE_FLAG_LABELS[k] ?? k,
      reason: (flags[k]?.reason && flags[k].reason!.trim()) || FEATURE_FLAG_DEFAULT_REASONS[k],
    }));
  }, [flags]);

  // Сигнатура текущего состояния — изменилась → баннер снова появляется.
  const signature = useMemo(() => {
    return disabledItems.map((i) => `${i.key}:${i.reason}`).join('|');
  }, [disabledItems]);

  const [dismissedFor, setDismissedFor] = useState<string | null>(() => {
    try {
      return localStorage.getItem(DISMISS_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // При смене сигнатуры — забываем dismiss (новое состояние ⇒ снова показать).
  useEffect(() => {
    if (dismissedFor && signature && dismissedFor !== signature) {
      setDismissedFor(null);
      try {
        localStorage.removeItem(DISMISS_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }
  }, [signature, dismissedFor]);

  if (disabledItems.length === 0) return null;
  if (dismissedFor === signature) return null;

  const handleDismiss = () => {
    setDismissedFor(signature);
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, signature);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="feature-flags-banner" role="status" aria-live="polite">
      <div className="ffb-content">
        <span className="ffb-icon" aria-hidden>⚠</span>
        <div className="ffb-text">
          {disabledItems.length === 1 ? (
            <>
              <strong>{disabledItems[0].label}</strong> временно недоступен.{' '}
              <span className="ffb-reason">{disabledItems[0].reason}</span>
            </>
          ) : (
            <>
              <strong>Часть функций временно недоступна:</strong>
              <ul className="ffb-list">
                {disabledItems.map((i) => (
                  <li key={i.key}>
                    <strong>{i.label}</strong> — {i.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
      <button
        className="ffb-close"
        onClick={handleDismiss}
        aria-label="Скрыть уведомление"
        title="Скрыть до следующего изменения"
      >
        ×
      </button>
    </div>
  );
};

export default FeatureFlagsBanner;
